import type { BangerPlayer, PlayResult } from '../spotify/player';

/** Why Apple Music can't host: not signed in (or the sign-in was closed), or MusicKit/the token failed. */
export type AppleError = 'apple_signin' | 'apple_failed';

const SCRIPT_URL = 'https://js-cdn.music.apple.com/musickit/v3/musickit.js';
const PLAY_CONFIRM_MS = 8_000;
/** MusicKit sometimes starts at 0 despite startTime; seek when it lands this far before the start point. */
const START_SLACK_S = 2;

let loading: Promise<void> | null = null;
let configured: Promise<MusicKit.MusicKitInstance> | null = null;

function loadScript(): Promise<void> {
  if (window.MusicKit) return Promise.resolve();
  loading ??= new Promise<void>((resolve, reject) => {
    document.addEventListener('musickitloaded', () => resolve(), { once: true });
    const s = document.createElement('script');
    s.src = SCRIPT_URL;
    s.async = true;
    s.onerror = () => { loading = null; s.remove(); reject(new Error('MusicKit failed to load')); };
    document.head.appendChild(s);
  });
  return loading;
}

/**
 * Loads and configures MusicKit with a developer token from the worker (memoized; retried after a failure).
 * Call early (e.g. when Apple Music is the chosen service) so the sign-in click opens its popup at once.
 */
export function prepareApple(): Promise<MusicKit.MusicKitInstance> {
  configured ??= (async () => {
    const res = await fetch('/api/apple/token');
    if (!res.ok) throw new Error(`token HTTP ${res.status}`);
    const { token } = (await res.json()) as { token: string };
    await loadScript();
    return window.MusicKit!.configure({ developerToken: token, app: { name: 'BANGER!', build: '1.0' } });
  })().catch((err) => {
    configured = null;
    throw err;
  });
  return configured;
}

export function appleSignedIn(): boolean {
  try {
    return window.MusicKit?.getInstance()?.isAuthorized ?? false;
  } catch {
    return false;
  }
}

/** Apple's sign-in popup. Call straight from a click: browsers block popups that aren't. */
export async function signInToApple(): Promise<boolean> {
  try {
    const music = await prepareApple();
    if (!music.isAuthorized) await music.authorize();
    return music.isAuthorized;
  } catch {
    return false;
  }
}

/** Spoiler hygiene: keep OS media widgets from showing the song. Best-effort; MusicKit may set it again. */
function hideMediaMetadata(): void {
  try {
    navigator.mediaSession.metadata = new MediaMetadata({ title: 'BANGER!', artist: '' });
  } catch { /* unsupported */ }
}

/** The display's Apple Music player. Needs a signed-in Apple Music subscriber. */
export async function createApplePlayer(
  onFatal: (e: AppleError) => void = () => {},
): Promise<BangerPlayer | { error: AppleError }> {
  let music: MusicKit.MusicKitInstance;
  try {
    music = await prepareApple();
  } catch {
    return { error: 'apple_failed' };
  }
  if (!music.isAuthorized) return { error: 'apple_signin' };

  let waiting: { id: string; settle: (r: PlayResult) => void } | null = null;
  const onState = (e: { state?: number }) => {
    if (e.state === MusicKit.PlaybackStates.playing) {
      hideMediaMetadata();
      if (waiting && music.nowPlayingItem?.id === waiting.id) waiting.settle('ok');
    }
  };
  const onError = () => waiting?.settle('unplayable');
  const onAuth = () => { if (!music.isAuthorized) onFatal('apple_signin'); };
  music.addEventListener('playbackStateDidChange', onState);
  music.addEventListener('mediaPlaybackError', onError);
  music.addEventListener('nowPlayingItemDidChange', hideMediaMetadata);
  music.addEventListener('authorizationStatusDidChange', onAuth);

  return {
    service: 'apple',
    deviceId: 'apple',
    async play(trackId, startMs) {
      waiting?.settle('ok'); // superseded; the caller ignores stale results
      let settle!: (r: PlayResult) => void;
      const result = new Promise<PlayResult>((r) => { settle = r; });
      const me = { id: trackId, settle };
      waiting = me;
      const timer = setTimeout(() => settle('unplayable'), PLAY_CONFIRM_MS);
      const startS = startMs / 1000;
      music.setQueue({ song: trackId, startTime: startS, startPlaying: true }).catch((err: unknown) => {
        const name = (err as { name?: string })?.name ?? '';
        // Blocked autoplay, a dropped sign-in or no network is the player's fault, never the song's.
        if (name === 'NotAllowedError' || !music.isAuthorized || !navigator.onLine) settle('device_error');
        else settle('unplayable');
      });
      const r = await result;
      clearTimeout(timer);
      if (waiting === me) waiting = null;
      if (r === 'ok' && startS > START_SLACK_S && music.currentPlaybackTime < startS - START_SLACK_S) {
        try { await music.seekToTime(startS); } catch { /* keep playing from where it is */ }
      }
      return r;
    },
    pause: async () => { music.pause(); },
    resume: () => music.play(),
    activate: async () => {},
    disconnect: () => {
      waiting?.settle('ok');
      music.removeEventListener('playbackStateDidChange', onState);
      music.removeEventListener('mediaPlaybackError', onError);
      music.removeEventListener('nowPlayingItemDidChange', hideMediaMetadata);
      music.removeEventListener('authorizationStatusDidChange', onAuth);
      void music.stop().catch(() => {});
    },
  };
}
